import { Link } from "react-router-dom";
import { TestIdClassNameProps } from "@app/types/Common";
import iconCheck from "@assets/icon-check.svg";
import iconPlus from "@assets/icon-plus-white.svg";
import Button from "@components/AccessibleComponents/Button";
import { Newsletter } from "@components/Newsletter/types/Newsletter";
import { useWindowSize } from "@src/app/hooks/useWindowSize";
import cx from "classnames";

export type HomepageCarouselNewsletterSlideProps = TestIdClassNameProps & {
  newsletter: Newsletter;
  titleClassName?: string;
  descriptionClassName?: string;
  wrapperClassName?: string;
  handleSelectedNewsletter: (
    action: "add" | "remove",
    newsletters: Newsletter
  ) => void;
  subscribed?: boolean;
};

export default function HomepageCarouselNewsletterSlide({
  testId,
  rootClassName,
  titleClassName,
  descriptionClassName,
  wrapperClassName,
  newsletter,
  handleSelectedNewsletter,
  subscribed = false,
}: HomepageCarouselNewsletterSlideProps): React.ReactElement {
  const { isScreenMD } = useWindowSize();
  const { thumbnail, label, updateTime, description, link } = newsletter;

  const NewsletterDescription = () => (
    <>
      <p
        className={cx(descriptionClassName, "mb-10 font-public-sans text-base")}
      >
        {description}
      </p>

      <Button
        data-testid={testId ? `${testId}-button` : undefined}
        className={cx(
          "mt-auto flex h-[40px] w-[40px] items-center justify-center rounded-full border border-black text-white",
          subscribed ? "bg-white" : "bg-black"
        )}
        onPress={() => {
          handleSelectedNewsletter(subscribed ? "remove" : "add", newsletter);
        }}
      >
        <img
          src={subscribed ? iconCheck : iconPlus}
          width={15}
          height={15}
          className="relative -left-[1px] aspect-square"
        />
      </Button>
    </>
  );

  return (
    <div
      className={cx(
        rootClassName,
        "h-full border-b border-gray-250 pb-4 last:border-none last:pb-0 md:border-none md:pb-0"
      )}
      data-testid={testId}
    >
      <div className="!flex h-full flex-col md:border md:border-black md:p-4">
        <div className={cx(wrapperClassName, "flex md:mb-6 md:pb-6")}>
          <img
            src={thumbnail}
            alt={label}
            width={75}
            height={75}
            className="aspect-square self-start object-contain pr-3"
          />
          <div>
            <div className="font-poppins text-4xs font-light tracking-[1px] text-gray-850">
              {updateTime.toUpperCase()}
            </div>

            <h3
              className={cx(
                titleClassName,
                "font-poppins text-3xl font-semibold text-gray-850"
              )}
            >
              {link ? (
                <Link to={link} className="hover:underline" reloadDocument>
                  {label}
                </Link>
              ) : (
                label
              )}
            </h3>

            {!isScreenMD ? <NewsletterDescription /> : null}
          </div>
        </div>

        {isScreenMD ? <NewsletterDescription /> : null}
      </div>
    </div>
  );
}
